import {
	Button,
	Dropdown,
	DropdownItem,
	DropdownMenu,
	DropdownTrigger,
	Spinner,
} from "@heroui/react";
import {useNavigate} from 'react-router-dom';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import repositories from '../repositories.jsx';
import toast from 'react-hot-toast';
import RenameModal from './RenameModal.jsx';
import DeleteModal from './DeleteModal.jsx';
import {useState} from 'react';
import CloneModal from './CloneModal.jsx';
import moment from 'moment';
import _ from 'lodash';
import {motion} from 'framer-motion';
import {appHelper} from '../helpers/AppHelper.jsx';

export default function ProjectCard({data = null, index = 0, ...props}) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();

	const [cloneModal, setCloneModal] = useState(false);
	const [renameModal, setRenameModal] = useState(false);
	const [deleteModal, setDeleteModal] = useState(false);

	const regenerateProject = useMutation({
		mutationFn: () => repositories.project.regenerate(data?.project_id),
		onSuccess: async () => {
			await queryClient.invalidateQueries(['projects']);
		},
		onError: (error) => {
			toast.error(error?.response?.data?.detail || 'Something went wrong')
		}
	})

	const isCompleted = data?.status === 'completed';
	const isInProgress = data?.status === 'in_progress';
	const isFailed = data?.status === 'failed';
	const statusMeta = {
		completed:   { label: 'Ready',      grad: ['#16c098', '#0a8f6c'], icon: 'eva eva-checkmark-outline' },
		in_progress: { label: 'Generating', grad: ['#f6a609', '#d97706'], icon: 'eva eva-clock-outline' },
		failed:      { label: 'Failed',     grad: ['#f23e5c', '#be123c'], icon: 'eva eva-alert-triangle-outline' },
	}[data?.status] || { label: _.startCase(data?.status || 'Draft'), grad: ['#94a3b8', '#64748b'], icon: 'hgi-stroke hgi-translate' };

	const openEditor = () => { if (isCompleted && data?.project_id) navigate(`/app/${data?.project_id}/edit`); };

	return (
		<>
			<motion.div
				initial={{ opacity: 0, y: 18 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ delay: index * 0.04, type: 'spring', stiffness: 260, damping: 26 }}
				whileHover={{ y: -6 }}
				className="group relative flex flex-col overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-[0_10px_40px_-26px_rgba(15,23,42,0.35)] hover:shadow-[0_26px_50px_-26px_rgba(90,10,224,0.4)] transition-shadow">

				{/* Cover = thumbnail */}
				<div
					style={{ backgroundImage: `url(${data?.thumbnail || '/demo_image.png'})` }}
					className="relative aspect-video bg-cover bg-no-repeat bg-center overflow-hidden bg-slate-100">
					<div className="absolute inset-0 backdrop-blur-md overflow-hidden">
						<img key={data?.thumbnail} src={data?.thumbnail || '/demo_image.png'} alt=""
							onError={(e) => { e.currentTarget.src = '/demo_image.png'; }}
							className="w-full h-full object-contain transition-transform duration-500 ease-out group-hover:scale-105" />
					</div>

					{/* generating overlay */}
					{isInProgress && (
						<div className="absolute inset-0 z-10 flex flex-col justify-center items-center bg-black/50 text-white backdrop-blur">
							<Spinner size="xl" className="scale-125" color="primary" />
							<p className="mt-5">Generating {data?.upload_percentage}...</p>
						</div>
					)}

					{/* failed overlay */}
					{isFailed && (
						<div className="absolute inset-0 z-10 flex flex-col justify-center items-center bg-black/50 text-white backdrop-blur p-6 text-center">
							<i className="eva eva-alert-triangle-outline text-4xl text-red-500"></i>
							<p className="mt-3">{data?.error_message || (data?.is_insufucient_balance ? 'Insufficient credits' : 'Video Generating failed!')}</p>
							<Button key="regenerate" isLoading={regenerateProject.isPending} className="mt-5 text-white border-white/50"
								onPress={regenerateProject.mutate} radius="full" variant="bordered"
								startContent={<i className="eva eva-refresh text-lg" />}>regenerate</Button>
						</div>
					)}

					{/* completed: duration chip, click-to-open, hover play */}
					{isCompleted && (
						<>
							<button type="button" onClick={openEditor} aria-label="Open in editor" className="absolute inset-0 z-10" />
							<div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
								<span className="grid h-14 w-14 place-items-center rounded-full bg-white/90 text-primary shadow-xl backdrop-blur scale-90 group-hover:scale-100 transition-transform duration-300">
									<i className="hgi-stroke hgi-pencil-edit-02 text-2xl" />
								</span>
							</div>
						</>
					)}

					{/* actions menu — glassy, always available */}
					<div className="absolute top-2.5 right-2.5 z-30">
						<Dropdown placement="bottom-end" closeOnSelect>
							<DropdownTrigger>
								<Button variant="solid" isIconOnly size="sm" radius="full"
									className="bg-black/40 text-white backdrop-blur-md border border-white/20 hover:bg-black/60 w-8 h-8 min-w-8">
									<i className="eva eva-more-vertical text-base"></i>
								</Button>
							</DropdownTrigger>
							<DropdownMenu aria-label="Project actions">
								<DropdownItem isDisabled={!data?.project_id} onPress={() => navigate(`${data?.project_id}/edit`)}
									startContent={<i className="hgi-stroke hgi-folder-edit text-xl" />} key="edit">Edit</DropdownItem>
								<DropdownItem startContent={<i className="hgi-stroke hgi-copy-01 text-xl" />} onPress={setCloneModal} key="clone">Clone</DropdownItem>
								<DropdownItem startContent={<i className="hgi-stroke hgi-align-right text-xl" />} onPress={setRenameModal} key="rename">Rename</DropdownItem>
								<DropdownItem key="delete" className="text-danger" color="danger" onPress={setDeleteModal}
									startContent={<i className="hgi-stroke hgi-delete-02 text-xl" />}>Delete</DropdownItem>
							</DropdownMenu>
						</Dropdown>
					</div>
				</div>

				{/* Body */}
				<div className="relative z-10 px-[18px] pb-[18px] flex flex-col flex-1">
					{/* overlapping status tile + render-progress chip */}
					<div className="-mt-[23px] mb-2.5 flex items-center gap-2">
						<div className="relative inline-grid h-11 min-w-[64px] px-3.5 place-items-center rounded-full border-2 border-white text-white"
							style={{ background: `linear-gradient(135deg, ${statusMeta.grad[0]}, ${statusMeta.grad[1]})` }}>
							{isInProgress
								? <span className="block h-5 w-5 rounded-full border-2 border-white/40 border-t-white animate-spin" />
								: isCompleted
									? <span className="text-[12px] font-bold leading-none tabular-nums">{appHelper.formatDuration(data?.video_duration)}</span>
									: <i className={`${statusMeta.icon} text-xl`} />}
						</div>
						{isCompleted && (data?.export_status === 'in_progress' || data?.export_status === 'rendering') && (
							<div className="inline-flex h-9 items-center gap-2 rounded-full border border-amber-200 bg-amber-50 pl-2.5 pr-3.5 text-amber-700 shadow-sm">
								<span className="block h-4 w-4 flex-none rounded-full border-2 border-amber-300 border-t-amber-600 animate-spin" />
								<span className="text-[12px] font-semibold leading-none tabular-nums">Rendering {_.round(data?.export_percentage, 2)}%</span>
							</div>
						)}
					</div>

					<h3 className="truncate text-[15.5px] font-semibold text-slate-800">{data?.project_name || 'Untitled'}</h3>
					<div className="mt-0.5 flex items-center gap-1.5 text-[12px]">
						<span className="font-semibold" style={{ color: statusMeta.grad[0] }}>{statusMeta.label}</span>
						{data?.created_at && <span className="text-slate-400">· {moment.utc(data?.created_at).local().fromNow()}</span>}
					</div>

					{/* language flow */}
					<div className="flex gap-2 items-center w-full mt-3">
						<div className="flex gap-1.5 items-center justify-center text-[13px] font-medium text-slate-600 bg-slate-100 grow rounded-full py-2 px-3 capitalize truncate">
							<i className="hgi-stroke hgi-translate text-slate-400" /><span className="truncate">{data?.original_language || 'Auto'}</span>
						</div>
						<span className="grid place-items-center h-6 w-6 flex-none rounded-full bg-slate-100 text-slate-400">
							<i className="eva eva-arrow-forward text-base" />
						</span>
						<div className="flex gap-1.5 items-center justify-center text-[13px] font-semibold text-primary bg-primary/10 grow rounded-full py-2 px-3 capitalize truncate">
							<i className="hgi-stroke hgi-translation text-primary/70" /><span className="truncate">{data?.translate_language || 'Auto'}</span>
						</div>
					</div>
				</div>
			</motion.div>

			<CloneModal data={data} openModal={cloneModal}/>
			<RenameModal data={data} openModal={renameModal}/>
			<DeleteModal data={data} openModal={deleteModal}/>
		</>
	)
}
